/**
 * Кэш ответов Strapi в памяти процесса.
 *
 * Глобальные данные (шапка, меню, футер) одинаковы для всех страниц, но
 * запрашивались на каждый запрос и занимали от 0.5 до 1.5 с — это ложилось
 * прямо в TTFB. Держим ответ ttlMs миллисекунд.
 *
 * Особенности:
 * - параллельные запросы за одним ключом дожидаются одного и того же промиса,
 *   поэтому всплеск трафика не превращается в пачку одинаковых запросов;
 * - если обновление упало, а прошлое значение есть, отдаём его: лучше слегка
 *   устаревшая шапка, чем пятисотка на всём сайте.
 */

/** Общий TTL для данных страниц: столько же живёт s-maxage в Cache-Control */
export const PAGE_DATA_TTL_MS = 15_000;

type Entry<T> = {
  value?: T;
  expiresAt: number;
  inFlight?: Promise<T>;
};

/**
 * Хранилище висит на globalThis, а не в модуле: Next собирает страницы и
 * api-роуты разными бандлами, поэтому модуль инициализируется несколько раз и
 * у каждого экземпляра была бы своя Map. Из-за этого сброс кэша из
 * /api/revalidate не действовал на данные, закэшированные при рендере страниц.
 */
const globalStore = globalThis as typeof globalThis & {
  __strapiTtlCache?: Map<string, Entry<unknown>>;
};

const store: Map<string, Entry<unknown>> = (globalStore.__strapiTtlCache ??=
  new Map());

export const cached = async <T>(
  key: string,
  ttlMs: number,
  load: () => Promise<T>,
): Promise<T> => {
  const now = Date.now();
  const entry = store.get(key) as Entry<T> | undefined;

  if (entry?.value !== undefined && entry.expiresAt > now) {
    return entry.value;
  }

  if (entry?.inFlight) return entry.inFlight;

  const inFlight = load()
    .then((value) => {
      store.set(key, { value, expiresAt: Date.now() + ttlMs });
      return value;
    })
    .catch((error) => {
      store.delete(key);

      if (entry?.value !== undefined) {
        store.set(key, { value: entry.value, expiresAt: Date.now() + ttlMs });
        return entry.value;
      }

      throw error;
    });

  store.set(key, { ...entry, expiresAt: entry?.expiresAt ?? 0, inFlight });

  return inFlight;
};

/** Сбрасывает кэш целиком или по ключу — пригодится для вебхука из Strapi */
export const invalidateCache = (key?: string) => {
  if (key) store.delete(key);
  else store.clear();
};
